import { useAuth0 } from "@auth0/auth0-react";
import { useEffect, useState } from "react";

import { getActivation, getActivationRecipe, getActivations } from "@/api";
import { useAudienceContext } from "@/context";
import { MetaCategory, MetaInterest } from "@/types";
import { defaultActivation } from "@/utils";

export const useActivation = (campaignId: string | undefined) => {
  const { state, dispatch } = useAudienceContext();
  const { getAccessTokenSilently } = useAuth0();

  useEffect(() => {
    const fetchActivation = async (id: string) => {
      dispatch({ type: "LOADING" });
      try {
        const accessToken = await getAccessTokenSilently();
        const { data, error } = await getActivation(accessToken, id);
        if (data) {
          dispatch({ type: "GET_ACTIVATION", payload: data });
        } else {
          dispatch({ type: "ERROR", payload: error?.message || "" });
        }
      } catch (error: any) {
        dispatch({ type: "ERROR", payload: error.message });
      }
    };

    if (!campaignId) {
      dispatch({
        type: "GET_ACTIVATION",
        payload: defaultActivation,
      });
    } else if (
      !state.currentActivation ||
      state.currentActivation.campaign_id !== campaignId
    ) {
      fetchActivation(campaignId);
    }
  }, [campaignId]);

  return {
    activation: state.currentActivation,
    loading: state.loading,
    error: state.error,
  };
};

export const useActivations = () => {
  const { state, dispatch } = useAudienceContext();
  const { getAccessTokenSilently } = useAuth0();

  const client = process.env.REACT_APP_CLIENT || "core";

  useEffect(() => {
    const fetchActivations = async () => {
      dispatch({ type: "LOADING" });
      try {
        const accessToken = await getAccessTokenSilently();
        const { data, error } = await getActivations(accessToken, client);
        if (data) {
          dispatch({
            type: "GET_ACTIVATION_LIST",
            payload: data,
          });
        } else {
          dispatch({ type: "ERROR", payload: error?.message || "" });
        }
      } catch (error: any) {
        dispatch({ type: "ERROR", payload: error.message });
      }
    };

    fetchActivations();
  }, []);

  return {
    activations: state.activations,
    loading: state.loading,
    error: state.error,
  };
};

export const useActivationRecipe = (campaignId: string | undefined) => {
  const { state, dispatch } = useAudienceContext();
  const { getAccessTokenSilently } = useAuth0();
  const [categories, setCategories] = useState<MetaCategory[]>([]);
  const [interests, setInterests] = useState<MetaInterest[]>([]);

  useEffect(() => {
    const fetchActivation = async (id: string) => {
      dispatch({ type: "LOADING" });
      try {
        const accessToken = await getAccessTokenSilently();
        const { data, error } = await getActivationRecipe(accessToken, id);
        if (data) {
          dispatch({ type: "GET_ACTIVATION", payload: data.activation });
          setCategories(data.categories);
          setInterests(data.interests);
        } else {
          dispatch({ type: "ERROR", payload: error?.message || "" });
        }
      } catch (error: any) {
        dispatch({ type: "ERROR", payload: error.message });
      }
    };

    if (campaignId) {
      fetchActivation(campaignId);
    }
  }, [campaignId]);

  return {
    activation: state.currentActivation,
    categories,
    interests,
    loading: state.loading,
    error: state.error,
  };
};
